import { FC } from 'react'
import { UseFormReturn } from 'react-hook-form'

import Field from '@/components/field'
import { ColorPicker } from '@/components/ui/color-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSvgUpload } from '@/hooks/useSvgUpload'

type BrandingFormData = {
  name: string
  logo: string | undefined
  color: string
}

type Props = {
  form: UseFormReturn<BrandingFormData>
  compact?: boolean
}

const BrandingForm: FC<Props> = ({ form, compact = false }) => {
  const { fileInputRef, colorOptions, handleFileChange, triggerFileInput } = useSvgUpload(result => {
    form.setValue('logo', result.svg)
  })

  const logo = form.watch('logo')

  return (
    <div className="space-y-4">
      <Field key="company-name" name="name" label="Company name" testId="company-name-input" />

      <div key="logo-upload">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Label className="text-sm font-medium text-foreground">Logo Upload</Label>
            {!compact && (
              <p className="mt-1 text-sm text-muted-foreground">
                Upload your company logo in SVG format. Your logo will appear in the customers&apos; browser as they
                complete their tasks.
              </p>
            )}
          </div>
        </div>
        <div
          onClick={triggerFileInput}
          className="flex items-center justify-center w-full h-48 overflow-hidden text-center transition-all duration-200 border-2 border-dashed rounded-lg cursor-pointer bg-secondary border-muted hover:border-primary/50"
        >
          {logo ? (
            <div className="flex items-center justify-center w-full h-full p-4">
              <div
                dangerouslySetInnerHTML={{ __html: logo }}
                className="w-full h-full max-h-[80%] max-w-[80%] flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-1 text-center text-muted-foreground">
              <p>Drop your SVG logo here</p>
              <p className="text-sm">or click to browse</p>
            </div>
          )}
        </div>
        <Input
          id="logo-upload"
          type="file"
          accept=".svg"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileChange}
          data-testid="logo-upload-input"
        />
      </div>

      <div key="brand-color">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div>
            <Label className="text-sm font-medium text-foreground">Primary Brand Color</Label>
            {!compact && (
              <p className="mt-1 text-sm text-muted-foreground">
                Set your brand color to maintain visual consistency across all Journey interfaces. This color will be
                used in the browser and throughout the platform.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center p-1 border rounded-md max-h-9">
              <ColorPicker
                value={form.watch('color')}
                onChange={value => form.setValue('color', value.toUpperCase())}
                onBlur={form.trigger.bind(null, 'color')}
                className="border-none w-7 h-7"
                data-testid="color-picker"
              />
            </div>
            <Input
              placeholder="#000000"
              {...form.register('color')}
              value={form.watch('color')}
              onChange={e => form.setValue('color', e.target.value.toUpperCase())}
              maxLength={7}
              className="w-24 font-mono uppercase bg-background"
              data-testid="color-picker-number-input"
            />
          </div>
        </div>
        {colorOptions.length > 0 && (
          <div className="p-4 rounded-lg bg-secondary">
            <p className="mb-3 text-sm text-muted-foreground">Colors detected in logo:</p>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((color, index) => (
                <div
                  key={index}
                  onClick={() => form.setValue('color', color)}
                  className="w-8 h-8 transition-all rounded-md cursor-pointer ring-border ring-1 hover:ring-2 hover:ring-primary hover:scale-105"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BrandingForm
